import { useCallback, useEffect, useRef, useState } from 'react';
import { bolusnotesApi } from '../services/api';
import { DEXCOM_GAP_MS } from '../utils/glucoseDisplay';

export interface GlucoseReading {
  glucose_value: number;
  trend: string;
  timestamp: string;
}

/**
 * Latest CGM reading per profile (dashboard API), with polling when any reading is stale.
 */
export function useAnalyticsCgmReadings(profileIds: string[]): Record<string, GlucoseReading | null> {
  const [byId, setById] = useState<Record<string, GlucoseReading | null>>({});
  const lastFetchRef = useRef(0);
  const byIdRef = useRef(byId);
  useEffect(() => {
    byIdRef.current = byId;
  }, [byId]);

  const fetchAll = useCallback(
    async (silent: boolean) => {
      if (profileIds.length === 0) {
        setById({});
        return;
      }
      try {
        if (!silent) lastFetchRef.current = 0;
        const data = await bolusnotesApi.dashboard.get();
        const profs = data.profiles || [];
        const next: Record<string, GlucoseReading | null> = {};
        for (const id of profileIds) {
          const p = profs.find((x: { id: string }) => x.id === id);
          next[id] = (p?.latest_reading as GlucoseReading | undefined) ?? null;
        }
        setById(next);
        lastFetchRef.current = Date.now();
      } catch {
        if (!silent) setById(Object.fromEntries(profileIds.map((id) => [id, null])));
      }
    },
    [profileIds]
  );

  useEffect(() => {
    fetchAll(false);
  }, [fetchAll]);

  useEffect(() => {
    if (profileIds.length === 0) return;
    const tick = setInterval(() => {
      const now = Date.now();
      let anyStale = false;
      for (const id of profileIds) {
        const r = byIdRef.current[id];
        const age = r?.timestamp ? now - new Date(r.timestamp).getTime() : DEXCOM_GAP_MS + 1;
        if (age > DEXCOM_GAP_MS) anyStale = true;
      }
      if (anyStale || now - lastFetchRef.current > 120000) {
        fetchAll(true);
      }
    }, 3000);
    return () => clearInterval(tick);
  }, [profileIds, fetchAll]);

  return byId;
}
